import { Grid } from '../../core/Grid.js';
import {
    Point,
    Position,
    Direction,
    Edge,
    Outside,
    positionKey,
    edgeKey,
    isInside,
    isRimEdge,
    turnCW,
    turnCCW,
    oppositeDir,
    edgeAdjacentCells
} from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';
import { stripSeparators } from '../stateSerialization.js';

/** The four directions, for scanning a cell's edges. */
const ALL_DIRECTIONS: Direction[] = ['N', 'E', 'S', 'W'];

// === Orbits-specific coordinate helpers ===

/** Get grid center point for a given grid size */
export function getGridCenter(size: number): Point {
    return { row: size / 2, col: size / 2 };
}

/** Chebyshev distance from point to grid center (determines ring) */
function chebyshevFromCenter(p: Point, center: Point): number {
    return Math.max(Math.abs(p.row - center.row), Math.abs(p.col - center.col));
}

/**
 * Check if edge is on an orbit boundary (rim or inter-ring).
 * Both endpoints must be at same Chebyshev distance from center.
 * For a grid of size N: distance N/2 = rim, distances 1 to N/2-1 = inter-ring orbits
 */
export function isOrbit(edge: Edge, gridSize: number): boolean {
    const center = getGridCenter(gridSize);
    const maxDist = gridSize / 2;
    const distA = chebyshevFromCenter(edge.pa, center);
    const distB = chebyshevFromCenter(edge.pb, center);
    return distA === distB && distA >= 1 && distA <= maxDist;
}

// Launcher at center
export interface Launcher {
    direction: Direction;
    clockwise: boolean;
}

type ExtendedQuadrant = 'N' | 'E' | 'S' | 'W' | 'NE' | 'SE' | 'SW' | 'NW';

// Get quadrant of a position relative to grid center
// Returns N/E/S/W for cardinal positions, NW/NE/SE/SW for diagonal positions
function getPositionQuadrant(pos: Position, center: Point): ExtendedQuadrant {
    // Use cell center (+0.5) relative to grid center
    const relRow = pos.row + 0.5 - center.row;
    const relCol = pos.col + 0.5 - center.col;

    const absRow = Math.abs(relRow);
    const absCol = Math.abs(relCol);

    if (absRow > absCol) {
        return relRow < 0 ? 'N' : 'S';
    } else if (absCol > absRow) {
        return relCol < 0 ? 'W' : 'E';
    } else {
        // Diagonal: |relRow| == |relCol|
        if (relRow < 0) {
            return relCol < 0 ? 'NW' : 'NE';
        } else {
            return relCol < 0 ? 'SW' : 'SE';
        }
    }
}

// Get the default orbitwise direction for a cell (the direction it should face when orbiting)
export function getOrbitwiseDirection(pos: Position, clockwise: boolean, center: Point): Direction {
    const quadrant = getPositionQuadrant(pos, center);

    if (clockwise) {
        // Clockwise orbit: sides move in CW direction, corners turn right
        switch (quadrant) {
            case 'N':  return 'E';  // Top side: move right
            case 'NE': return 'S';  // Top-right corner: turn down
            case 'E':  return 'S';  // Right side: move down
            case 'SE': return 'W';  // Bottom-right corner: turn left
            case 'S':  return 'W';  // Bottom side: move left
            case 'SW': return 'N';  // Bottom-left corner: turn up
            case 'W':  return 'N';  // Left side: move up
            case 'NW': return 'E';  // Top-left corner: turn right
        }
    } else {
        // Counter-clockwise orbit: sides move in CCW direction, corners turn left
        switch (quadrant) {
            case 'N':  return 'W';  // Top side: move left
            case 'NW': return 'S';  // Top-left corner: turn down
            case 'W':  return 'S';  // Left side: move down
            case 'SW': return 'E';  // Bottom-left corner: turn right
            case 'S':  return 'E';  // Bottom side: move right
            case 'SE': return 'N';  // Bottom-right corner: turn up
            case 'E':  return 'N';  // Right side: move up
            case 'NE': return 'W';  // Top-right corner: turn left
        }
    }
}

// What the ship hits next from a position
export type NextObject =
    | { type: 'digit'; digit: number }
    | { type: 'launcher' }
    | { type: 'exit' }
    | null;

// Result of path simulation
export interface SimulationResult {
    path: Position[];
    exitPoint: Position | null;  // Position outside grid where ship exited
    hit: NextObject;
    endDirection: Direction | null;  // Direction when stopping (for continuing simulation)
    teleportIndices?: number[];  // Path indices that are teleport destinations (for rendering line breaks)
}

export class OrbitsGame {
    private grid: Grid;
    private launcher: Launcher = { direction: 'N', clockwise: true };
    private gates: Set<string> = new Set();  // Set of edgeKey strings
    private dismissedGates: Set<string> = new Set();  // dropped by the last placement

    // Convenience accessors for grid subsystems
    protected get gs() { return this.grid.serializer; }

    private digitPositions: Map<number, Position> = new Map();
    private digitCells: Set<string> = new Set();

    // Computed path
    private shipPath: Position[] = [];
    private digitsVisited: number[] = [];
    private pathExited: boolean = false;

    // Computed state for badges
    private nextFromDigit: Map<number, NextObject> = new Map();
    private launcherHitsFirst: number | null = null;

    // Selected digit for path display
    private selectedDigit: number | null = null;

    constructor(grid: Grid) {
        this.grid = grid;
        this.initializeDigitPositions();
        this.recalculatePath();
    }

    /** Grid center point (for orbit calculations) */
    private get center(): Point {
        return getGridCenter(this.grid.size);
    }

    private initializeDigitPositions(): void {
        const positions = this.grid.getAllDigitPositions();
        for (const [digit, pos] of positions) {
            this.digitPositions.set(digit, pos);
            this.digitCells.add(positionKey(pos));
        }
    }

    getGrid(): Grid { return this.grid; }
    getLauncher(): Launcher { return this.launcher; }
    getGates(): Set<string> { return this.gates; }
    getShipPath(): Position[] { return this.shipPath; }
    getDigitsVisited(): number[] { return this.digitsVisited; }
    hasPathExited(): boolean { return this.pathExited; }
    getDigitPositions(): Map<number, Position> { return this.digitPositions; }
    getSelectedDigit(): number | null { return this.selectedDigit; }
    getNextFromDigit(digit: number): NextObject { return this.nextFromDigit.get(digit) ?? null; }
    getLauncherHitsFirst(): number | null { return this.launcherHitsFirst; }

    /** Set the launch direction, keeping the current orbit (clockwise) setting. */
    setLauncher(direction: Direction): void {
        this.launcher.direction = direction;
        this.recalculatePath();
    }

    /** Flip the orbit between clockwise and counter-clockwise. */
    toggleClockwise(): void {
        this.launcher.clockwise = !this.launcher.clockwise;
        this.recalculatePath();
    }

    toggleSelectedDigit(digit: number): void {
        this.selectedDigit = this.selectedDigit === digit ? null : digit;
    }

    /** Show the path onward from `digit`, without the toggle-off. Used by the
     *  keyboard trace, which lights up each digit it runs across. */
    selectDigit(digit: number): void {
        this.selectedDigit = digit;
    }

    clearSelectedDigit(): void {
        this.selectedDigit = null;
    }

    hasGate(edge: Edge): boolean {
        return this.gates.has(edgeKey(edge));
    }

    /**
     * Whether a gate may be placed on this orbit edge. False when the edge sits
     * next to a digit cell (such slots are shown hollow in the UI).
     */
    isGatePlaceable(edge: Edge): boolean {
        if (!isOrbit(edge, this.grid.size)) return false;
        const adjacentCells = edgeAdjacentCells(edge);
        return !adjacentCells.some(c => this.digitCells.has(positionKey(c)));
    }

    toggleGate(edge: Edge): boolean {
        if (!this.isGatePlaceable(edge)) return false;

        const key = edgeKey(edge);
        this.dismissedGates.clear();

        if (this.gates.has(key)) {
            this.gates.delete(key);
        } else {
            this.gates.add(key);
            // Activating a gate deactivates any sibling it would conflict with,
            // so corner conflicts and multiple exit gates can never occur.
            this.deactivateConflicts(edge);
        }

        this.recalculatePath();
        return true;
    }

    /**
     * Gates the last placement threw out to keep its invariants — a rival exit, or
     * the other orientation of a corner. The renderer flashes these red as they
     * retract, so the board doesn't just silently lose a gate.
     */
    getDismissedGates(): Set<string> {
        return this.dismissedGates;
    }

    /**
     * Remove gates that would conflict with a freshly-activated gate. For an exit
     * (rim) gate, any other exit gate is removed. For an orbit gate, any gate that
     * would share a corner (perpendicular, same cell) is removed. The game keeps
     * the invariant that no warning state is ever reachable.
     */
    private deactivateConflicts(edge: Edge): void {
        const key = edgeKey(edge);

        if (isRimEdge(edge)) {
            for (const exit of this.getExitGates()) {
                const k = edgeKey(exit);
                if (k !== key) this.dismiss(k);
            }
            return;
        }

        // After adding `edge`, any corner conflict must involve it (the invariant
        // held beforehand), so drop every conflicting gate except this one.
        const conflicts = this.getCornerConflictGates();
        if (conflicts.has(key)) {
            for (const k of conflicts) {
                if (k !== key) this.dismiss(k);
            }
        }
    }

    /** Drop a gate the newly-placed one displaced, remembering it for the UI. */
    private dismiss(key: string): void {
        this.gates.delete(key);
        this.dismissedGates.add(key);
    }

    /**
     * Get all exit gates (rim edges).
     */
    getExitGates(): Edge[] {
        const exits: Edge[] = [];
        for (const key of this.gates) {
            const edge = this.grid.deserializeEdge(key);
            if (edge && isRimEdge(edge)) {
                exits.push(edge);
            }
        }
        return exits;
    }

    /**
     * Check if there are multiple exit gates (a warning condition).
     */
    hasMultipleExitGates(): boolean {
        return this.getExitGates().length > 1;
    }

    /**
     * Get gates that share an orbit corner with another gate.
     * Corner conflict: two gates that share an adjacent cell AND have different orientations.
     * Parallel gates on the same side don't share a cell.
     * Chained gates between rings share a cell but have the same orientation.
     */
    getCornerConflictGates(): Set<string> {
        const conflicts = new Set<string>();
        const gatesByCell = new Map<string, { key: string; orient: 'h' | 'v' }[]>();

        for (const key of this.gates) {
            const edge = this.grid.deserializeEdge(key);
            if (!edge || isRimEdge(edge)) continue;

            // Add to buckets for each adjacent cell
            for (const cell of edgeAdjacentCells(edge)) {
                const ck = positionKey(cell);
                if (!gatesByCell.has(ck)) gatesByCell.set(ck, []);
                gatesByCell.get(ck)!.push({ key, orient: edge.orient });
            }
        }

        // Corner conflict: gates at same cell with different orientations (perpendicular)
        for (const [, gates] of gatesByCell) {
            if (gates.length < 2) continue;

            const hasH = gates.some(g => g.orient === 'h');
            const hasV = gates.some(g => g.orient === 'v');
            if (hasH && hasV) {
                gates.forEach(g => conflicts.add(g.key));
            }
        }

        return conflicts;
    }

    /**
     * Check if there are any gate warnings (multiple exits or corner conflicts).
     */
    hasGateWarnings(): boolean {
        return this.hasMultipleExitGates() || this.getCornerConflictGates().size > 0;
    }

    protected recalculatePath(): void {
        this.shipPath = [];
        this.digitsVisited = [];
        this.pathExited = false;
        this.nextFromDigit.clear();
        this.launcherHitsFirst = null;

        // Get starting cell and direction from launcher
        const start = this.getStartState();
        let currentPos = start.cell;
        let currentDir: Direction | null = start.direction;

        const visitedDigits = new Set<number>();
        const maxSegments = this.grid.digitCount + 1; // At most N digits + 1 exit

        for (let segment = 0; segment < maxSegments; segment++) {
            // Simulate from current position to next object
            // Don't check for launcher return (null) since we're computing the full path
            const result = this.simulatePath(currentPos, currentDir, null);

            // Add path segment (skip first cell if not first segment to avoid duplicates)
            const pathToAdd = segment === 0 ? result.path : result.path.slice(1);
            this.shipPath.push(...pathToAdd);

            // Handle what we hit
            if (result.hit?.type === 'exit') {
                this.pathExited = true;
                break;
            } else if (result.hit?.type === 'digit') {
                const digit = result.hit.digit;
                if (!visitedDigits.has(digit)) {
                    visitedDigits.add(digit);
                    this.digitsVisited.push(digit);

                    if (this.launcherHitsFirst === null) {
                        this.launcherHitsFirst = digit;
                    }
                }
                // Continue from this digit
                currentPos = this.digitPositions.get(digit)!;
                currentDir = result.endDirection;
            } else {
                // Loop or max steps reached
                break;
            }
        }

        // Compute what each digit hits next
        this.computeNextFromDigits();
    }

    /**
     * Compute what the path hits after each digit.
     */
    private computeNextFromDigits(): void {
        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const next = this.simulateFromDigit(digit);
            this.nextFromDigit.set(digit, next);
        }
    }

    /**
     * Get the launcher cell (the first cell of the path, in front of the launcher).
     */
    getLauncherCell(): Position {
        return this.getStartState().cell;
    }

    /**
     * Hook called when the ship enters a new cell during simulation.
     * Override in subclasses to add teleportation behavior.
     */
    protected onShipEntersCell(
        cell: Position, _direction: Direction, _justTeleported: boolean
    ): { cell: Position; teleported: boolean } {
        return { cell, teleported: false };
    }

    /**
     * Unified path simulation from a starting position.
     * @param startPos - Starting cell position
     * @param startDir - Starting direction (if null, computed from orbitwise)
     * @param launcherState - Launcher position and direction to check for return (null to skip check)
     */
    private simulatePath(
        startPos: Position,
        startDir: Direction | null,
        launcherState: { cell: Position; direction: Direction } | null
    ): SimulationResult {
        const orbitwise = this.launcher.clockwise;

        const path: Position[] = [{ ...startPos }];
        const teleportIndices: number[] = [];
        let cell = { ...startPos };
        let direction = startDir ?? getOrbitwiseDirection(cell, orbitwise, this.center);
        let justTeleported = false;

        const visitedStates = new Set<string>();
        const maxSteps = 500;

        for (let step = 0; step < maxSteps; step++) {
            // Check gates and apply movement rules
            const hasFrontGate = this.hasGateInDirection(cell, direction);
            const hasPortsideGate = this.hasGateInDirection(cell, turnCCW(direction));
            const hasStarboardGate = this.hasGateInDirection(cell, turnCW(direction));

            let nextPos: Position | Outside;

            if (hasFrontGate) {
                nextPos = this.grid.move(cell, direction);
            } else if (hasPortsideGate && hasStarboardGate) {
                nextPos = this.grid.move(cell, direction);
            } else if (hasStarboardGate) {
                direction = turnCW(direction);
                continue; // Turn only, no movement this step
            } else if (hasPortsideGate) {
                direction = turnCCW(direction);
                continue; // Turn only, no movement this step
            } else {
                direction = getOrbitwiseDirection(cell, orbitwise, this.center);
                nextPos = this.grid.move(cell, direction);
            }

            // Check if we've exited the grid
            if (!isInside(nextPos)) {
                const exitPos = nextPos as Outside;
                return { path, teleportIndices, exitPoint: { row: exitPos.row, col: exitPos.col }, hit: { type: 'exit' }, endDirection: direction };
            }

            cell = nextPos;

            // Teleportation hook (vortex gates)
            const moveResult = this.onShipEntersCell(cell, direction, justTeleported);
            if (moveResult.teleported) {
                path.push({ ...cell }); // Record entry gate cell
                cell = moveResult.cell;
                teleportIndices.push(path.length); // Next push will be the teleport destination
                justTeleported = true;
            } else {
                justTeleported = false;
            }

            // Check for loop
            const stateKey = `${cell.row},${cell.col},${direction}`;
            if (visitedStates.has(stateKey)) {
                return { path, teleportIndices, exitPoint: null, hit: null, endDirection: direction };
            }
            visitedStates.add(stateKey);

            path.push({ ...cell });

            // Check if we hit the launcher cell (must match both position AND direction)
            if (launcherState &&
                cell.row === launcherState.cell.row &&
                cell.col === launcherState.cell.col &&
                direction === launcherState.direction) {
                return { path, teleportIndices, exitPoint: null, hit: { type: 'launcher' }, endDirection: direction };
            }

            // Check if we hit a digit
            for (const [d, dpos] of this.digitPositions) {
                if (dpos.row === cell.row && dpos.col === cell.col) {
                    return { path, teleportIndices, exitPoint: null, hit: { type: 'digit', digit: d }, endDirection: direction };
                }
            }
        }

        return { path, teleportIndices, exitPoint: null, hit: null, endDirection: direction }; // Max steps reached
    }

    /**
     * Get the path from the launcher cell to the first object hit.
     */
    getPathFromLauncher(): SimulationResult {
        const start = this.getStartState();
        return this.simulatePath(start.cell, start.direction, null);
    }

    /**
     * Simulate the path starting from a digit and find what it hits next.
     */
    simulateFromDigit(digit: number): NextObject {
        const startPos = this.digitPositions.get(digit);
        if (!startPos) return null;
        const launcherState = this.getStartState();
        return this.simulatePath(startPos, null, { cell: launcherState.cell, direction: launcherState.direction }).hit;
    }

    /**
     * Get the path segment from a digit to the next object (for visualization).
     */
    getPathFromDigit(digit: number): SimulationResult {
        const startPos = this.digitPositions.get(digit);
        if (!startPos) return { path: [], exitPoint: null, hit: null, endDirection: null };
        const launcherState = this.getStartState();
        return this.simulatePath(startPos, null, { cell: launcherState.cell, direction: launcherState.direction });
    }

    private getStartState(): { cell: Position; direction: Direction } {
        // Center point is at (size/2, size/2)
        // The four center cells are relative to this:
        //   (center-1, center-1) NW, (center-1, center) NE
        //   (center, center-1) SW,   (center, center) SE
        // Launcher direction determines which row/col, clockwise determines which cell
        // Clockwise orbit: top→east, right→south, bottom→west, left→north
        const cw = this.launcher.clockwise;
        const c = this.center;
        const rowAbove = c.row - 1;
        const rowBelow = c.row;
        const colLeft = c.col - 1;
        const colRight = c.col;

        switch (this.launcher.direction) {
            case 'N':
                // North: row above center. Clockwise enters east cell, CCW enters west cell
                return { cell: { row: rowAbove, col: cw ? colRight : colLeft }, direction: cw ? 'E' : 'W' };
            case 'S':
                // South: row at center. Clockwise enters west cell, CCW enters east cell
                return { cell: { row: rowBelow, col: cw ? colLeft : colRight }, direction: cw ? 'W' : 'E' };
            case 'E':
                // East: col at center. Clockwise enters south cell, CCW enters north cell
                return { cell: { row: cw ? rowBelow : rowAbove, col: colRight }, direction: cw ? 'S' : 'N' };
            case 'W':
                // West: col left of center. Clockwise enters north cell, CCW enters south cell
                return { cell: { row: cw ? rowAbove : rowBelow, col: colLeft }, direction: cw ? 'N' : 'S' };
        }
    }

    private hasGateInDirection(cell: Position, dir: Direction): boolean {
        const edge = this.grid.getEdgeFromPosition(cell, dir);
        return this.gates.has(edgeKey(edge));
    }

    // ── Steering (keyboard trace) ──────────────────────────────────────

    /**
     * The direction the ship leaves `cell` in, having arrived heading `heading`.
     * Mirrors the movement rules in {@link simulatePath}: a gate in front — or gates
     * on both sides, which cancel — sends it straight on; a single side gate turns
     * it that way, and it then leaves through that gate (the gate it turned toward
     * is now in front); a bare cell follows the orbit.
     */
    shipExitDirection(cell: Position, heading: Direction): Direction {
        return this.exitDirectionWith(cell, heading, null);
    }

    /**
     * {@link shipExitDirection} evaluated as if the gate on `toggled` were flipped,
     * so a candidate can be judged before committing to it. Models the corner rule
     * as well: activating a gate on a cell that already holds a perpendicular one
     * displaces every other gate on that cell.
     */
    private exitDirectionWith(cell: Position, heading: Direction, toggled: Edge | null): Direction {
        const toggledKey = toggled ? edgeKey(toggled) : null;
        const adding = !!toggled && !this.gates.has(toggledKey!);

        const displaces = adding && !isRimEdge(toggled!) && ALL_DIRECTIONS.some(d => {
            const e = this.grid.getEdgeFromPosition(cell, d);
            return e.orient !== toggled!.orient && this.gates.has(edgeKey(e));
        });

        const has = (d: Direction): boolean => {
            const key = edgeKey(this.grid.getEdgeFromPosition(cell, d));
            if (key === toggledKey) return adding;
            return displaces ? false : this.gates.has(key);
        };

        const front = has(heading);
        const port = has(turnCCW(heading));
        const starboard = has(turnCW(heading));

        if (front || (port && starboard)) return heading;
        if (starboard) return turnCW(heading);
        if (port) return turnCCW(heading);
        return getOrbitwiseDirection(cell, this.launcher.clockwise, this.center);
    }

    /**
     * Steer the ship so that, arriving at `cell` heading `fromDir`, it leaves heading
     * `moveDir` — make it do what the player just did. Flips at most one gate, and
     * prefers the board with fewer of them: walking straight past a gate that would
     * have turned the ship takes that gate away rather than stacking another on top,
     * and taking one of a cancelling pair removes the other.
     *
     * Returns true if a gate changed — false when the ship already goes that way, or
     * when no single gate bends it there (a reversal, for one: nothing turns the ship
     * back on itself in a single cell).
     */
    steerShip(cell: Position, fromDir: Direction, moveDir: Direction): boolean {
        if (this.shipExitDirection(cell, fromDir) === moveDir) return false;

        // Nearest intent first: the gate you went through, the one opposing it, then
        // the pair either side of it — which, on a straight hop, are the gates that
        // would have turned the ship off your path.
        const order: Direction[] = [
            moveDir, oppositeDir(moveDir), turnCW(moveDir), turnCCW(moveDir),
        ];
        const seen = new Set<string>();
        const candidates: Edge[] = [];
        for (const dir of order) {
            const edge = this.grid.getEdgeFromPosition(cell, dir);
            const key = edgeKey(edge);
            if (seen.has(key)) continue;
            seen.add(key);
            if (this.isGatePlaceable(edge)) candidates.push(edge);
        }

        // Removals before additions, so the gate count stays as low as the rule allows.
        for (const wantActive of [true, false]) {
            for (const edge of candidates) {
                if (this.gates.has(edgeKey(edge)) !== wantActive) continue;
                if (this.exitDirectionWith(cell, fromDir, edge) === moveDir) {
                    return this.toggleGate(edge);
                }
            }
        }
        return false;
    }

    /**
     * The opening hop of a trace, where the ship's heading — and so whether it would
     * need to turn — isn't known yet. Changing rings always needs a gate on the
     * boundary being crossed, so open an empty slot there; a gate already in place is
     * left alone, and a hop along a ring (which crosses no orbit edge) does nothing.
     */
    openTrace(cell: Position, moveDir: Direction): boolean {
        const edge = this.grid.getEdgeFromPosition(cell, moveDir);
        if (!this.isGatePlaceable(edge) || this.hasGate(edge)) return false;
        return this.toggleGate(edge);
    }

    checkWin(): boolean {
        // Gate warnings (corner conflicts or multiple exit gates) invalidate any win
        if (this.hasGateWarnings()) return false;
        if (!this.pathExited) return false;
        if (this.digitsVisited.length !== this.grid.digitCount) return false;
        for (let i = 0; i < this.grid.digitCount; i++) {
            if (this.digitsVisited[i] !== this.grid.minDigit + i) return false;
        }
        return true;
    }

    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const gateCount = this.gates.size;

        // Count empty cells visited by ship
        const emptyCells = new Set<string>();
        for (const pos of this.shipPath) {
            const key = positionKey(pos);
            if (!this.digitCells.has(key)) {
                emptyCells.add(key);
            }
        }

        return calculateScore(gateCount, emptyCells.size);
    }

    /** Scoring-rules overlay: ship-visited empty cells (secondary). Gates (primary)
     *  are redrawn in amber by the renderer. */
    getScoringHighlight(): ScoringHighlight {
        const secondaryCells: Position[] = [];
        const seen = new Set<string>();
        for (const pos of this.shipPath) {
            const key = positionKey(pos);
            if (this.digitCells.has(key) || seen.has(key)) continue;
            seen.add(key);
            secondaryCells.push({ ...pos });
        }
        return { secondaryCells };
    }

    getStatus(): string {
        const boldRed = (text: string) =>
            `<span style="color:#e94560;font-weight:bold">${text}</span>`;

        if (this.checkWin()) {
            return 'You win! Ship visited all digits in order and escaped!';
        }

        // Gate warnings (red elements) take priority
        if (this.hasGateWarnings()) {
            if (this.hasMultipleExitGates() && this.getCornerConflictGates().size > 0) {
                return `${boldRed('Warning')}: multiple exit gates and corner gate conflicts!`;
            }
            if (this.hasMultipleExitGates()) {
                return `${boldRed('Warning')}: multiple exit gates placed!`;
            }
            return `${boldRed('Warning')}: gates conflict at the same corner!`;
        }

        const hint = 'Click orbit edges to place gates. Click digits to see the ship\'s path.';

        // Name the leg still to be built: from wherever the ship last got right, to
        // the digit it should reach next.
        const correctCount = this.getCorrectDigitCount();
        if (correctCount < this.grid.digitCount) {
            const nextDigit = this.grid.minDigit + correctCount;
            const from = correctCount === 0
                ? 'the launcher'
                : `digit ${this.grid.minDigit + correctCount - 1}`;
            return `Make a path from ${from} to digit ${nextDigit}. ${hint}`;
        }

        if (!this.pathExited) {
            return `Make a path from digit ${this.grid.maxDigit} to an exit gate on the grid edge. ${hint}`;
        }

        return '';
    }

    getCorrectDigitCount(): number {
        let count = 0;
        for (let i = 0; i < this.digitsVisited.length; i++) {
            if (this.digitsVisited[i] === this.grid.minDigit + i) count++;
            else break;
        }
        return count;
    }

    isStarted(): boolean {
        return this.gates.size > 0;
    }

    reset(): void {
        this.launcher = { direction: 'N', clockwise: true };
        this.gates.clear();
        this.selectedDigit = null;
        this.recalculatePath();
    }

    /**
     * Jumpstart a solution: aim the ship at the first digit. First tries a
     * straight line of "front" gates out from the launch cell (the ship rides
     * the line then the natural orbit curves it on). For trickier digits it
     * steers the ship cell-by-cell (BFS) — go straight, turn via a side gate, or
     * ride the orbit — until it arrives at a cell whose orbit step enters digit
     * 1. Every candidate is verified against the path simulator; may fail on a
     * few grids where no such route exists.
     */
    autoStart(): boolean {
        if (this.isStarted()) return false;

        const min = this.grid.minDigit;
        const d1 = this.digitPositions.get(min);
        if (!d1) return false;

        if (this.tryStraightRay()) return true;
        if (this.trySteerToDigit(d1)) return true;

        this.reset();
        return false;
    }

    /** Straight ray of front-gates out from each launcher; orbit completes the approach. */
    private tryStraightRay(): boolean {
        const size = this.grid.size;
        const dirs: Direction[] = ['N', 'E', 'S', 'W'];
        for (const dir of dirs) {
            for (const cw of [true, false]) {
                this.reset();
                this.launcher = { direction: dir, clockwise: cw };
                this.recalculatePath();
                const rayDir = this.launchRayDirection(dir, cw);
                let cell: Position = this.getLauncherCell();
                for (let step = 1; step < size; step++) {
                    const edge = this.grid.getEdgeFromPosition(cell, rayDir);
                    if (!this.toggleGate(edge) || !this.gates.has(edgeKey(edge))) break;
                    const next = this.grid.move(cell, rayDir);
                    if (!isInside(next)) break;
                    cell = next;
                    if (this.getCorrectDigitCount() >= 1 && !this.hasGateWarnings()) return true;
                }
            }
        }
        return false;
    }

    /** Direction the ship first travels for a given launcher (mirrors getStartState). */
    private launchRayDirection(dir: Direction, cw: boolean): Direction {
        switch (dir) {
            case 'N': return cw ? 'E' : 'W';
            case 'S': return cw ? 'W' : 'E';
            case 'E': return cw ? 'S' : 'N';
            case 'W': return cw ? 'N' : 'S';
        }
    }

    /** Try each launcher; BFS-steer the ship to a cell whose orbit step enters digit 1. */
    private trySteerToDigit(d1: Position): boolean {
        const dirs: Direction[] = ['N', 'E', 'S', 'W'];
        for (const dir of dirs) {
            for (const cw of [true, false]) {
                const gates = this.steerBFS(d1, dir, cw);
                if (!gates) continue;
                this.reset();
                this.launcher = { direction: dir, clockwise: cw };
                for (const edge of gates) this.gates.add(edgeKey(edge));
                this.recalculatePath();
                if (this.getCorrectDigitCount() >= 1 && !this.hasGateWarnings()) return true;
            }
        }
        return false;
    }

    /** A placeable gate edge in a direction (orbit line, not digit-adjacent), or null. */
    private steerGateEdge(cell: Position, dir: Direction): Edge | null {
        const edge = this.grid.getEdgeFromPosition(cell, dir);
        if (!isOrbit(edge, this.grid.size)) return null;
        if (edgeAdjacentCells(edge).some(c => this.digitCells.has(positionKey(c)))) return null;
        return edge;
    }

    /**
     * BFS over (cell, heading) steering states for the given launcher. At each
     * state the ship can ride the orbit (no gate), go straight (front gate), or
     * turn (side gate). Goal: a cell whose orbit step lands on digit 1 (the final
     * move needs no gate, since gates can't sit next to a digit). Returns the
     * gate edges placed along the route, or null.
     */
    private steerBFS(d1: Position, dir: Direction, cw: boolean): Edge[] | null {
        this.launcher = { direction: dir, clockwise: cw };
        const center = this.center;
        const start = this.getStartState();
        const stateKey = (p: Position, d: Direction) => `${p.row},${p.col},${d}`;
        const startKey = stateKey(start.cell, start.direction);

        const parent = new Map<string, { prev: string; gate: Edge | null }>();
        parent.set(startKey, { prev: '', gate: null });
        const queue: Array<{ cell: Position; direction: Direction }> = [start];

        while (queue.length > 0) {
            const { cell, direction } = queue.shift()!;
            const curKey = stateKey(cell, direction);

            const orbitDir = getOrbitwiseDirection(cell, cw, center);
            const orbitNext = this.grid.move(cell, orbitDir);
            if (isInside(orbitNext) && orbitNext.row === d1.row && orbitNext.col === d1.col) {
                const edges: Edge[] = [];
                let k = curKey;
                while (k && k !== startKey) {
                    const p = parent.get(k)!;
                    if (p.gate) edges.push(p.gate);
                    k = p.prev;
                }
                if (edges.length > 0) return edges;
            }

            // Successors: ride the orbit (no gate), or steer with a gate.
            const moves: Array<{ ndir: Direction; gate: Edge | null }> = [{ ndir: orbitDir, gate: null }];
            for (const td of [direction, turnCW(direction), turnCCW(direction)]) {
                const edge = this.steerGateEdge(cell, td);
                if (edge) moves.push({ ndir: td, gate: edge });
            }

            for (const { ndir, gate } of moves) {
                const ncell = this.grid.move(cell, ndir);
                if (!isInside(ncell)) continue;
                if (this.digitCells.has(positionKey(ncell))) continue;  // don't ram a digit early
                const nk = stateKey(ncell, ndir);
                if (parent.has(nk)) continue;
                parent.set(nk, { prev: curKey, gate });
                queue.push({ cell: ncell, direction: ndir });
            }
        }
        return null;
    }

    /**
     * Serialize game state to string format.
     * Format: "NC,A4A5,B5B6" - launcher (direction + C/X for clockwise), then gate edges.
     */
    serialize(): string {
        const parts: string[] = [];

        // Serialize launcher: direction + C(lockwise) or X(counter-clockwise)
        const cwChar = this.launcher.clockwise ? 'C' : 'X';
        parts.push(`${this.launcher.direction}${cwChar}`);

        // Serialize gates
        for (const gateKey of this.gates) {
            const edge = this.grid.deserializeEdge(gateKey);
            if (edge) {
                parts.push(this.gs.serializeEdgeCells(edge));
            }
        }

        return parts.join('');
    }

    /**
     * Deserialize game state from string format.
     */
    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        const compact = stripSeparators(serialized);
        let i = 0;
        // The launcher is a 2-char prefix (length ≡ 2 mod 4); gates are 4 chars each.
        if (compact.length % 4 === 2) {
            const launcherStr = compact.slice(0, 2);
            if (/^[NESW][CX]$/.test(launcherStr)) {
                this.launcher = { direction: launcherStr[0] as Direction, clockwise: launcherStr[1] === 'C' };
            }
            i = 2;
        }
        for (; i + 4 <= compact.length; i += 4) {
            const edge = this.grid.deserializeEdgeCells(compact.slice(i, i + 4));
            if (edge) {
                this.gates.add(edgeKey(edge));
            }
        }

        this.recalculatePath();
    }
}
